import { Button, Loader, Modal } from "@mantine/core";
import { IconAlertTriangle, IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { PredictionComputeSummary } from "#/types/prediction";

export type ComputeState =
  | { kind: "closed" }
  | { kind: "running" }
  | { kind: "done"; summary: PredictionComputeSummary }
  | { kind: "failed"; error: string };

interface Props {
  clientId: number;
  state: ComputeState;
  onClose: () => void;
}

/** The numbers shown, in order: predictions first, then the shipments that group them. */
const ROWS: { key: string; value: (summary: PredictionComputeSummary) => number }[] = [
  { key: "subClients", value: (summary) => summary.subClients },
  { key: "predicted", value: (summary) => summary.predicted },
  { key: "notPredictable", value: (summary) => summary.notPredictable },
  { key: "created", value: (summary) => summary.created },
  { key: "refreshed", value: (summary) => summary.refreshed },
  { key: "outcomes", value: (summary) => summary.outcomes },
  { key: "shipments", value: (summary) => summary.shipments.shipments },
  { key: "grouped", value: (summary) => summary.shipments.grouped },
];

/**
 * Same window as the PRV run modal: a loader while the computation runs, then
 * the result as a short list, with a way straight to the Predicted Deliveries
 * page. Cannot be dismissed while running.
 */
const PredictionComputeModal = ({ clientId, state, onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const running = state.kind === "running";

  return (
    <Modal
      opened={state.kind !== "closed"}
      onClose={() => {
        if (!running) onClose();
      }}
      centered
      size="sm"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={!running}
      overlayProps={{ blur: 4, backgroundOpacity: 0.35 }}
      title={t("clients.prediction.compute.modal.title")}
    >
      {state.kind === "running" && (
        <div className="flex flex-col items-center gap-5 py-2">
          <Loader color="var(--client-color)" size="lg" />
          <span className="text-sm font-medium text-gray-900" aria-live="polite">
            {t("clients.prediction.compute.modal.running")}
          </span>
        </div>
      )}

      {state.kind === "done" && (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
            {ROWS.map(({ key, value }) => (
              <div key={key} className="contents">
                <dt className="text-gray-600">
                  {t(`clients.prediction.compute.modal.rows.${key}`)}
                </dt>
                <dd className="text-right font-semibold text-gray-900 tabular-nums">
                  {value(state.summary)}
                </dd>
              </div>
            ))}
          </dl>
          {!state.summary.routing.available && (
            <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
              <IconAlertTriangle size={18} className="mt-0.5 shrink-0 text-orange-600" />
              <span>{t(`predictedDeliveries.routing.${state.summary.routing.reason}`)}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
            <Button variant="outline" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button
              rightSection={<IconExternalLink size={16} />}
              onClick={() => {
                onClose();
                void navigate(`/fleet/predicted-deliveries?clientId=${clientId}`);
              }}
            >
              {t("clients.prediction.openPage")}
            </Button>
          </div>
        </div>
      )}

      {state.kind === "failed" && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-red-800">
                {t("clients.prediction.compute.modal.failed")}
              </span>
              <span className="text-sm text-red-700">{state.error || "--"}</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" color="red" onClick={onClose}>
              {t("common.close")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PredictionComputeModal;
